import { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  RadioGroup,
  Typography,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio, { RadioProps } from "@mui/material/Radio";
import {
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";

import { Colors } from "../../../configs/colors";
import {
  CardIcon,
  CashIcon,
  CheckIcon,
  MonthlyIcon,
  PayLaterIcon,
} from "../../../assets/icons/paymentTypes";
import { IPaymentModalData } from "./checkinSummary";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { CustomButton } from "../../atoms";
import { PaymentTypeEnum } from "../../../typings/enum/paymentTypes";
import useCart from "../../../hooks/useCart";
import { CashSection } from "./sections/cash";
import { CardSection } from "./sections/card";
import { CheckSection } from "./sections/check";
import { StoreCreditSection } from "./sections/storeCredit";
import MonthlySection from "./sections/monthly";
import { formatCurrency } from "../../../util/common-functions";
import { IOSSwitch } from "../../atoms/custom-toggle-button/custom-toggle-button";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import {
  addStoreCreditAction,
  getCustomerByIdAction,
  getStoreCreditHistoryAction,
} from "../../../redux/actions/customerAction";
import LaterSection from "./sections/later";
import ManagerCodeModal from "../../common/manager-code-modal/ManagerCodeModal";

export interface IPaymentProps {
  onCheckout(data: IPaymentModalData | null): void;
  onPaymentTypeChange(paymentType: PaymentTypeEnum): void;
  paymentType: PaymentTypeEnum | null;
  pickupAmount?: number;
  remainingBalance?: number;
  // Whether selecting "Later" requires manager-code approval (via
  // ManagerCodeModal) before it's committed. Defaults to true so every
  // existing call site (PickupModal.tsx, AddCardWarningModal.tsx) keeps
  // today's gated behaviour without any changes. The normal Check-in
  // checkout flow (payment-modal.tsx) opts out by passing `false`, since
  // Later there should behave exactly like every other payment type.
  requireManagerApprovalForLater?: boolean;
  // ID of the single bill this payment applies to, so a Later-payment
  // manager approval can be attached to that bill's history timeline.
  // Omit when the payment spans multiple bills (e.g. AddCardWarningModal's
  // BoxStorageModal flow) - there is no single unambiguous bill to attach to.
  billId?: number;
}
interface IPaymentTypeProps {
  value: string;
  label: string;
  icon: "CASH" | "CARD" | "CHECK" | "MONTHLY" | "LATER" | "STORE";
  width?: string;
  isSelected?: boolean;
  disabled?: boolean;
}

export const PaymentType: FC<IPaymentTypeProps> = ({
  value,
  label,
  icon,
  width = "auto",
  isSelected = false,
  disabled = false,
}) => {
  const icons = {
    CASH: <CashIcon color={isSelected ? Colors.PRIMARY : Colors.MAIN_GRAY} />,
    CARD: <CardIcon color={isSelected ? Colors.PRIMARY : Colors.MAIN_GRAY} />,
    CHECK: <CheckIcon color={isSelected ? Colors.PRIMARY : Colors.MAIN_GRAY} />,
    MONTHLY: (
      <MonthlyIcon color={isSelected ? Colors.PRIMARY : Colors.MAIN_GRAY} />
    ),
    LATER: (
      <PayLaterIcon color={isSelected ? Colors.PRIMARY : Colors.MAIN_GRAY} />
    ),
    STORE: (
      <PayLaterIcon color={isSelected ? Colors.PRIMARY : Colors.MAIN_GRAY} />
    ),
  };

  const CustomRadio: FC<RadioProps> = (props) => (
    <Radio
      {...props}
      icon={<RadioButtonUnchecked sx={{ fontSize: "2rem" }} />}
      checkedIcon={
        <CheckCircle sx={{ color: Colors.PRIMARY, fontSize: "2rem" }} />
      }
    />
  );

  return (
    <FormControlLabel
      value={value}
      control={<CustomRadio />}
      label={
        <Box
          sx={{
            width: width,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "0.7rem",
          }}
        >
          <Box
            sx={{
              borderRight: `0.0785714286rem solid ${isSelected ? Colors.PRIMARY : Colors.ACCENT_GRAY_DISABLED
                }`,
              height: "7.2rem",
              marginTop: "-0.5rem",
              marginRight: "0.1rem",
              marginLeft: "0.1rem",
              display: "fixed",
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0.3rem",
              paddingLeft: "1rem",
            }}
            data-label={`payment-type-${label}`}
          >
            <Box
              sx={{
                width: { xs: "2.5rem", sm: "3.5rem" },
                height: { xs: "2.5rem", sm: "3.5rem" },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {icons[icon]}
            </Box>

            <Typography
              sx={{
                fontSize: "var(--fs-2xs)",
                color: isSelected ? Colors.PRIMARY : Colors.TEXT_GRAY_DARK,
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              {label}
            </Typography>
          </Box>
        </Box>
      }
      sx={{
        p: "0.15rem 0.714285714rem",
        paddingTop: "0.55rem",
        background: Colors.WHITE,
        borderRadius: "0.394285714rem",
        border: `0.0785714286rem solid ${isSelected ? Colors.PRIMARY : Colors.ACCENT_GRAY_DISABLED
          }`,
        minWidth: "8.95714286rem",
        minHeight: "6.34357143rem",
        flex: 1,
        margin: "0 !important",
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        "&>span:last-child": {
          mx: "auto !important",
        },
        "&>span:first-child": {
          padding: "0.2rem !important",
        },
      }}
      disabled={disabled}
    />
  );
};

// export const CardSection: FC<{ billValue?: number }> = ({ billValue }) => {

const Payment: FC<IPaymentProps> = ({
  onCheckout,
  onPaymentTypeChange,
  paymentType,
  pickupAmount,
  requireManagerApprovalForLater = true,
  billId,
}) => {
  const {
    billsLoading,
    paymentHalfDetails,
    paymentCreateLoading,
    cartMode,
    billById,
  } = useAppSelector((state) => state.cart);
  const { cartTotal, totalDiscountAmount } = useCart();
  const billTotal =
    cartMode === "check-in"
      ? cartTotal - totalDiscountAmount
      : pickupAmount ?? 0;
  const [paidAmount, setPaidAmount] = useState(0);
  const paidTotal = paymentHalfDetails.reduce(
    (acc, item) => acc + item.halfPaymentAmount,
    0
  );
  const remainAmount =
    (cartMode === "check-in"
      ? paymentHalfDetails.reduce(
        (acc, item) => acc + item.fullpaymentAmount,
        0
      )
      : pickupAmount ?? 0) - paidTotal;
  const [balance, setBalance] = useState(0);

  const [saveBalanceToCredits, setSaveBalanceToCredits] = useState(
    paymentType === PaymentTypeEnum.STORE_CREDIT ||
      paymentType === PaymentTypeEnum.LATER ||
      paymentType === PaymentTypeEnum.MONTHLY ||
      (paymentType === PaymentTypeEnum.CASH && balance <= 0) ||
      (paymentType === PaymentTypeEnum.CARD && balance <= 0)
      ? false
      : false
  );

  const dispatch = useAppDispatch();
  const { showSnackbar } = useSnackbarContext();
  const { customer } = useAppSelector((state) => state.customer);
  const { user } = useAppSelector((state) => state.auth);
  const { addStoreCreditLoading } = useAppSelector((state) => state.customer);
  const [trackrem, setTrackrem] = useState(0);
  const [hasSetTrackrem, setHasSetTrackrem] = useState(false);

  const amountInputRefs = {
    [PaymentTypeEnum.CASH]: useRef(null),
    [PaymentTypeEnum.CARD]: useRef(null),
    [PaymentTypeEnum.CHECK]: useRef(null),
    [PaymentTypeEnum.MONTHLY]: useRef(null),
    [PaymentTypeEnum.LATER]: useRef(null),
    [PaymentTypeEnum.STORE_CREDIT]: useRef(null),
  };

  // Disable Store Credit option if balance is $0
  const isStoreCreditDisabled = (customer?.storeCreditBalance ?? 0) <= 0;

  // Monthly is only enabled when customer has MONTHLY_CHARGE in their Pay At preference
  const payAtPrefs = customer?.customerPayAtPref ?? [];
  const isMonthlyDisabled = !payAtPrefs.includes("MONTHLY_CHARGE");

  // Later and Monthly are both "invoice terms, pay me later" on the backend
  // (same NOT_CLEARED handling, same outstanding-balance tracking) - the only
  // difference is which bucket they land in on financial/outstanding reports.
  // A monthly-billed customer's unpaid bill must always be tagged Monthly for
  // that reporting to be correct, so Later is disabled for them entirely -
  // picking it by mistake wouldn't lose the money, but would mis-file it.
  const isLaterDisabled = payAtPrefs.includes("MONTHLY_CHARGE");

  // If Store Credit becomes disabled while selected, switch to Cash
  useEffect(() => {
    if (isStoreCreditDisabled && paymentType === PaymentTypeEnum.STORE_CREDIT) {
      onPaymentTypeChange(PaymentTypeEnum.CASH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStoreCreditDisabled]);

  // If Monthly becomes disabled while selected, switch to Cash
  useEffect(() => {
    if (isMonthlyDisabled && paymentType === PaymentTypeEnum.MONTHLY) {
      onPaymentTypeChange(PaymentTypeEnum.CASH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMonthlyDisabled]);

  // If Later becomes disabled (customer turns out to be monthly-billed) while selected, switch to Cash
  useEffect(() => {
    if (isLaterDisabled && paymentType === PaymentTypeEnum.LATER) {
      onPaymentTypeChange(PaymentTypeEnum.CASH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLaterDisabled]);

  const [isLaterAccessModalOpen, setIsLaterAccessModalOpen] = useState(false);

  const handlePaymentTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value === PaymentTypeEnum.LATER) {
      if (requireManagerApprovalForLater) {
        setIsLaterAccessModalOpen(true);
        return;
      }
      onPaymentTypeChange(PaymentTypeEnum.LATER);
      return;
    }
    onPaymentTypeChange(event.target.value as PaymentTypeEnum);
  };

  const handleOnPaidAmountChange = (value: number) => {
    setPaidAmount(value);
  };

  useEffect(() => {
    setTrackrem((billById?.billAmount ?? 0) - billTotal);
  }, []);

  useEffect(() => {
    if (!hasSetTrackrem && paymentHalfDetails.length > 0) {
      setTrackrem(
        paymentHalfDetails[0]?.fullpaymentAmount -
        billTotal -
        paymentHalfDetails[0]?.halfPaymentAmount
      );
      setHasSetTrackrem(true);
    }
  }, [paymentHalfDetails, hasSetTrackrem, billTotal]);

  const hasAddedTrackrem = useRef(false);

  const [checkNumber, setCheckNumber] = useState("");

  // Card/Cheque have no "change" mechanism like Cash does, so an overpayment
  // on those methods must be routed to store credit - if the cashier hasn't
  // enabled that toggle, the excess would otherwise just be lost.
  const isOverpayingWithoutStoreCredit =
    (paymentType === PaymentTypeEnum.CARD ||
      paymentType === PaymentTypeEnum.CHECK) &&
    balance > 0 &&
    !saveBalanceToCredits;

  const handleOnCheckout = () => {
    if (!paymentType) {
      showSnackbar("Please select a payment method", "error");
      return;
    }

    // Check Number Validation
    if (paymentType === PaymentTypeEnum.CHECK && !checkNumber.trim()) {
      showSnackbar("Please enter the check number", "error");
      return;
    }

    if (isOverpayingWithoutStoreCredit) {
      showSnackbar(
        "Amount exceeds the invoice total. Enable 'Save balance to store credits' to continue.",
        "error"
      );
      return;
    }

    // Add store credit validation
    if (paymentType === PaymentTypeEnum.STORE_CREDIT) {
      if (paidAmount > (customer?.storeCreditBalance ?? 0)) {
        showSnackbar(`Insufficient store credit balance. Available: $${(customer?.storeCreditBalance ?? 0).toFixed(2)}`, "error");
        return;
      }
      if (paidAmount <= 0) {
        showSnackbar("Please enter a valid amount to pay", "warning");
        return;
      }
    }

    if (saveBalanceToCredits) {
      if (balance > 0) {
        if (user && customer) {
          dispatch(
            addStoreCreditAction({
              storeUserId: user.id,
              customerId: customer.id,
              paymentType: paymentType,
              amount: balance,
            })
          ).then((res: any) => {
            if (res.payload.status === "SUCCESS") {
              dispatch(getCustomerByIdAction(String(customer?.id)));
              customer && dispatch(getStoreCreditHistoryAction(customer.id));
              showSnackbar("Store Credit added successfully", "success");
            } else {
              showSnackbar(res.payload.message, "error");
            }
          });
        } else {
          showSnackbar("Customer not found to save store credit", "error");
        }
      } else {
        showSnackbar("Please check the balance again!", "warning");
      }
    }

    // Compute final paid amount
    let finalPaidAmount = paidAmount;

    console.log(paidAmount, " paidAmount");

    if (
      (cartMode === "pickup" && billTotal === 0) ||
      (paidAmount >= billTotal && trackrem > 0 && !hasAddedTrackrem.current)
    ) {
      finalPaidAmount += trackrem;
      hasAddedTrackrem.current = true;
    }

    onCheckout({
      paymentMode: paymentType,
      paidAmount: finalPaidAmount,
      // The raw typed/stepper amount, before the trackrem fold-up above -
      // see IPaymentModalData.rawPaidAmount for why callers showing a
      // change-due confirmation need this instead of finalPaidAmount.
      rawPaidAmount: paidAmount,
      checkNumber: checkNumber,
      isStoreCredit: saveBalanceToCredits,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4.03571429rem",
        justifyContent: "space-between",
        height: "50.8571429rem",
        mt: { xs: "-3rem", sm: "0" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1.57142857rem",
          justifyContent: "space-between",
        }}
      >
        <Box
          sx={{
            background: Colors.BACKGROUND_GRAY,
            borderRadius: "0.39428571428rem",
            p: "1.2rem 2.14285714rem",
            //   width: "53.8571429rem",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "0.75rem",
            height: "13.0714286rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "var(--fs-lg)",
              fontWeight: "bold",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            Payment Type
          </Typography>
          <FormControl>
            <RadioGroup
              value={paymentType ?? ""}
              onChange={handlePaymentTypeChange}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "0.75rem",
                justifyContent: "space-between",
                //   width: "49.5714286rem",
                width: "100%",
              }}
            >
              <PaymentType
                value={PaymentTypeEnum.CARD}
                label="Card"
                icon="CARD"
                width="12.5rem"
                isSelected={paymentType === PaymentTypeEnum.CARD}
              />
              <PaymentType
                value={PaymentTypeEnum.CASH}
                label="Cash"
                icon="CASH"
                width="12.5rem"
                isSelected={paymentType === PaymentTypeEnum.CASH}
              />
              <PaymentType
                value={PaymentTypeEnum.CHECK}
                label="Check"
                icon="CHECK"
                width="12.5rem"
                isSelected={paymentType === PaymentTypeEnum.CHECK}
              />
              <PaymentType
                value={PaymentTypeEnum.MONTHLY}
                label="Monthly"
                icon="MONTHLY"
                width="12.5rem"
                isSelected={paymentType === PaymentTypeEnum.MONTHLY}
                disabled={isMonthlyDisabled}
              />
              <PaymentType
                value={PaymentTypeEnum.LATER}
                label="Later"
                icon="LATER"
                width="12.5rem"
                isSelected={paymentType === PaymentTypeEnum.LATER}
                disabled={isLaterDisabled}
              />
              <PaymentType
                value={PaymentTypeEnum.STORE_CREDIT}
                label="Store Credit"
                icon="STORE"
                width="12.5rem"
                isSelected={paymentType === PaymentTypeEnum.STORE_CREDIT}
                disabled={isStoreCreditDisabled}
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </Box>
      {/* {PayOptionSections[paymentType]} */}

      {!paymentType && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "23.8571429rem",
            background: Colors.BACKGROUND_GRAY,
            borderRadius: "0.357142857rem",
          }}
        >
          <Typography
            sx={{
              fontSize: "var(--fs-md)",
              fontWeight: 500,
              color: Colors.MAIN_GRAY,
            }}
          >
            Select a payment method above to continue
          </Typography>
        </Box>
      )}

      {paymentType === PaymentTypeEnum.CARD && (
        <CardSection
          billValue={
            cartMode === "pickup"
              ? remainAmount === 0 && paidTotal === 0
                ? billTotal
                : billTotal
              : remainAmount === 0 && paidTotal === 0
                ? billTotal
                : remainAmount
          }
          paidAmount={paidAmount}
          returnPaidAmount={(value) => handleOnPaidAmountChange(value)}
          ref={amountInputRefs[PaymentTypeEnum.CARD]}
          returnBalance={setBalance}
        />
      )}
      {paymentType === PaymentTypeEnum.CASH && (
        <CashSection
          billValue={
            cartMode === "pickup"
              ? remainAmount === 0 && paidTotal === 0
                ? billTotal
                : billTotal
              : remainAmount === 0 && paidTotal === 0
                ? billTotal
                : remainAmount
          }
          returnPaidAmount={(value) => handleOnPaidAmountChange(value)}
          balance={balance}
          returnBalance={setBalance}
          saveBalanceToCredits={saveBalanceToCredits}
        />
      )}

      {paymentType === PaymentTypeEnum.CHECK && (
        <CheckSection
          billValue={
            cartMode === "pickup"
              ? remainAmount === 0 && paidTotal === 0
                ? billTotal
                : billTotal
              : remainAmount === 0 && paidTotal === 0
                ? billTotal
                : remainAmount
          }
          paidAmount={paidAmount}
          ref={amountInputRefs[PaymentTypeEnum.CHECK]}
          returnBalance={setBalance}
          returnPaidAmount={(value) => handleOnPaidAmountChange(value)}
          returnCheckNumber={setCheckNumber}
          saveBalanceToCredits={saveBalanceToCredits}
        />
      )}

      {paymentType === PaymentTypeEnum.MONTHLY && (
        <MonthlySection
          billValue={
            cartMode === "pickup"
              ? remainAmount === 0 && paidTotal === 0
                ? billTotal
                : billTotal
              : remainAmount === 0 && paidTotal === 0
                ? billTotal
                : remainAmount
          }
          returnPaidAmount={(value) => handleOnPaidAmountChange(value)}
        />
      )}

      {paymentType === PaymentTypeEnum.LATER && (
        <LaterSection
          billValue={
            cartMode === "pickup"
              ? remainAmount === 0 && paidTotal === 0
                ? billTotal
                : billTotal
              : remainAmount === 0 && paidTotal === 0
                ? billTotal
                : remainAmount
          }
          returnPaidAmount={(value) => handleOnPaidAmountChange(value)}
        />
      )}

      {paymentType === PaymentTypeEnum.STORE_CREDIT && (
        <StoreCreditSection
          billValue={
            cartMode === "pickup"
              ? remainAmount === 0 && paidTotal === 0
                ? billTotal
                : billTotal
              : remainAmount === 0 && paidTotal === 0
                ? billTotal
                : remainAmount
          }
          paidAmount={paidAmount}
          returnPaidAmount={(value) => handleOnPaidAmountChange(value)}
          ref={amountInputRefs[PaymentTypeEnum.STORE_CREDIT]}
          paymentType="withdraw"
          readonly={false}
        />
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.57142857rem",
          mt: "-2rem",
        }}
      >
        <FormControlLabel
          checked={
            paymentType === PaymentTypeEnum.STORE_CREDIT ||
              paymentType === PaymentTypeEnum.LATER ||
              paymentType === PaymentTypeEnum.MONTHLY ||
              (paymentType === PaymentTypeEnum.CASH && balance <= 0) ||
              (paymentType === PaymentTypeEnum.CHECK && balance <= 0) ||
              (paymentType === PaymentTypeEnum.CARD && balance <= 0)
              ? false
              : saveBalanceToCredits
          }
          onChange={() => setSaveBalanceToCredits(!saveBalanceToCredits)}
          control={<IOSSwitch />}
          label={
            <Typography
              sx={{
                fontSize: "var(--fs-base)",
                lineHeight: "1.71428571rem",
                fontWeight: 400,
                mr: "0.964285714rem",
              }}
            >
              Save balance to store credits
            </Typography>
          }
          labelPlacement="start"
          sx={{
            display:
              paymentType === PaymentTypeEnum.STORE_CREDIT ||
                paymentType === PaymentTypeEnum.LATER ||
                paymentType === PaymentTypeEnum.MONTHLY
                ? "none"
                : "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "1rem",
          }}
          disabled={
            paymentType === PaymentTypeEnum.STORE_CREDIT ||
            paymentType === PaymentTypeEnum.LATER ||
            paymentType === PaymentTypeEnum.MONTHLY ||
            (paymentType === PaymentTypeEnum.CASH && balance <= 0) ||
            (paymentType === PaymentTypeEnum.CHECK && balance <= 0) ||
            (paymentType === PaymentTypeEnum.CARD && balance <= 0)
          }
        />
        <Box
          sx={{ display: "flex", flexDirection: "row", gap: "1.37678571rem" }}
        >
          <Box
            sx={{
              background: Colors.BACKGROUND_GRAY,
              padding: "2.14285714rem 1.80071429rem",
              height: "5.85714286rem",
              borderRadius: "0.571428571rem",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "24.6946457rem",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: Colors.TEXT_GRAY_DARK,
                fontSize: "var(--fs-base)",
              }}
            >
              Net Total:
            </Typography>
            <Typography
              sx={{
                fontWeight: "bold",
                color: Colors.TEXT_GRAY_DARK,
                fontSize: "var(--fs-xl)",
              }}
            >
              {formatCurrency({
                amount: billTotal,
                prefix: "$",
              })}
            </Typography>
          </Box>
          <Box
            sx={{
              background: Colors.BACKGROUND_GRAY,
              padding: "2.14285714rem 1.80071429rem",
              height: "5.85714286rem",
              borderRadius: "0.571428571rem",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              width: "24.6946457rem",
            }}
          >
            <Typography
              sx={{
                fontWeight: "bold",
                color: Colors.TEXT_GRAY_DARK,
                fontSize: "var(--fs-base)",
              }}
            >
              Remaining:
            </Typography>
            <Typography
              sx={{
                fontWeight: "bold",
                color: Colors.TEXT_GRAY_DARK,
                fontSize: "var(--fs-xl)",
              }}
            >
              {cartMode === "pickup"
                ? // In pickup mode `billTotal` (== `pickupAmount`) is already
                  // net of every payment made so far this session - it's
                  // recomputed from the bill's live `paidAmount` in redux
                  // after each successful payment (see
                  // PickupModal.updateHalfOrFullPayment), unlike check-in's
                  // fixed cart total. Subtracting `paidTotal` again here
                  // would double-count what was just paid.
                  formatCurrency({ amount: billTotal, prefix: "$" })
                : formatCurrency({
                    amount:
                      remainAmount === 0 && paidTotal === 0
                        ? billTotal
                        : remainAmount,
                    prefix: "$",
                  })}
            </Typography>
          </Box>
          <CustomButton
            onClick={handleOnCheckout}
            disabled={
              !paymentType ||
              (paidAmount === 0 && billTotal > 0) ||
              billsLoading ||
              addStoreCreditLoading ||
              paymentCreateLoading ||
              isOverpayingWithoutStoreCredit
            }
            sx={{
              background: Colors.PRIMARY,
              color: Colors.WHITE,
              textTransform: "capitalize",
              height: "5.85714286rem",
              fontSize: "var(--fs-2xl)",
              width: "32.9285714rem",
              fontWeight: "bold",
              borderRadius: "0.714285714rem",
              "&:hover": {
                background: Colors.PRIMARY,
              },
            }}
          >
            {
              billsLoading || addStoreCreditLoading || paymentCreateLoading ? (
                <CircularProgress size={30} color="inherit" />
              ) : (remainAmount === 0
                ? billTotal
                : remainAmount - totalDiscountAmount) -
                paidAmount <=
                0 ? (
                "Checkout"
              ) : (
                "Pay"
              ) //temporary added the label as "Pay"
            }
          </CustomButton>
        </Box>
      </Box>

      <ManagerCodeModal
        open={isLaterAccessModalOpen}
        onClose={() => setIsLaterAccessModalOpen(false)}
        title="Manager Approval"
        task="defer payment (Later)"
        onDone={() => {
          onPaymentTypeChange(PaymentTypeEnum.LATER);
          setIsLaterAccessModalOpen(false);
        }}
        actionType="LATER_PAYMENT_ACCESS"
        requestedByUserId={user?.id}
        billId={billId}
      />
    </Box>
  );
};

export default Payment;
